import { t } from "i18next";
import {
    ACTIVATION_STATUS_AVAILABLE,
    ACTIVATION_STATUS_BEFORE,
    ACTIVATION_STATUS_EXPIRED,
    ACTIVATION_STATUS_OFFLINE,
    ACTIVATION_STATUS_ONLINE,
} from "../consts/constants";
import { License } from "../types/License";
import { addDays, addMonths, addYears, convertUtcToLocalDate, formatUtcToLocalDateTime } from "./DateUtils";
import { User } from "../types/User";
import { LicenseForOutput } from "../types/LicenseForOutput";
import { ReactElement } from "react";
import { Licenseuser } from "../types/Licenseuser";

/**
 * アクティベーションステータスを判別し、対応する文言を返す
 * @param license ライセンス情報
 * @returns アクティベーションステータス名
 */
export function getActivationStatusName(license: License) {
    let status = license.activation_status;
    // [利用開始前]もしくは[有効期限切れ]は日時データから判断する
    if (new Date() < convertUtcToLocalDate(license.activation_date)) {
        status = ACTIVATION_STATUS_BEFORE;
    } else if (new Date() > convertUtcToLocalDate(license.expire_date)) {
        status = ACTIVATION_STATUS_EXPIRED;
    } else if (status === ACTIVATION_STATUS_BEFORE) {
        // 有効期限内であるが、ステータスが「利用開始前」のままの場合、未アクティベーションとして扱う
        status = ACTIVATION_STATUS_AVAILABLE;
    }
    return t(`ActivationStatus.${status}`);
}

/**
 * ライセンスのステータス文言を決定する
 * @param license
 * @returns
 */
export function checkLicenseStaus(license: License): ReactElement {
    if (new Date() < convertUtcToLocalDate(license.activation_date)) {
        return <span className="text-gray-500">{t("licenseStatus.beforeActivationDate")}</span>;
    }
    if (license.permanent_flg) {
        if (
            license.activation_status == ACTIVATION_STATUS_ONLINE ||
            license.activation_status == ACTIVATION_STATUS_OFFLINE
        ) {
            return <span>{t("licenseStatus.inUse")}</span>;
        } else {
            return <span className="text-green-600">{t("licenseStatus.available")}</span>;
        }
    } else {
        if (new Date() > convertUtcToLocalDate(license.expire_date)) {
            return <span className="text-red-700">{t("licenseStatus.expired")}</span>;
        } else if (
            license.activation_status == ACTIVATION_STATUS_ONLINE ||
            license.activation_status == ACTIVATION_STATUS_OFFLINE
        ) {
            return <span>{t("licenseStatus.inUse")}</span>;
        } else {
            return <span className="text-green-600">{t("licenseStatus.available")}</span>;
        }
    }
}

/**
 * [ユーザー・ライセンス管理画面用]
 * データベースから取得したライセンス情報リストを、出力用のライセンス情報リストへ変換
 * @param licenseList DBから取得したライセンス情報リスト
 * @param userList DBから取得したユーザー情報リスト
 */
export function convertLicenseListForOutput(licenseList: License[], userList: User[]) {
    const outputList: LicenseForOutput[] = licenseList.map((l) => {
        const user = specifyUser(l.user_id, userList);
        const licenseForOutput: LicenseForOutput = {
            license_id: l.license_id,
            package_name: l.package_name,
            license_key: l.license_key,
            activation_date: formatUtcToLocalDateTime(l.activation_date),
            expire_date: formatUtcToLocalDateTime(l.expire_date),
            email: l.email,
            free_trial_flg: l.free_trial_flg,
            activation_status: l.activation_status,
            activation_status_string: getActivationStatusName(l),
            activation_record_date: formatUtcToLocalDateTime(l.activation_record_date),
            computer_name: l.computer_name,
            sid: l.sid,
            user_id: l.user_id,
            family_name: user ? user.family_name : "",
            given_name: user ? user.given_name : "",
            organization: user ? user.organization : "",
            owner_group_id: l.owner_group_id,
            payment_history_id: l.payment_history_id,
            permanent_flg: l.permanent_flg,
            support_start_date: l.support_start_date,
            support_expiration_date: l.support_expiration_date,
            last_support_expiration_date: l.last_support_expiration_date,
        };
        return licenseForOutput;
    });
    return outputList;
}

/**
 * [ライセンス統計画面用]
 * データベースから取得したライセンス情報リストを、出力用のライセンス情報リストへ変換
 * @param licenseList DBから取得したライセンス情報リスト
 */
export function convertLicenseuserListForOutput(licenseList: Licenseuser[]) {
    const outputList: Licenseuser[] = licenseList.map((l) => {
        const licenseForOutput: Licenseuser = {
            license_id: l.license_id,
            license_key: l.license_key,
            package_id: l.package_id,
            package_name: l.package_name,
            activation_date: formatUtcToLocalDateTime(l.activation_date),
            expire_date: formatUtcToLocalDateTime(l.expire_date),
            user_id: l.user_id,
            owner_group_id: l.owner_group_id,
            activation_status: l.activation_status,
            activation_record_date: formatUtcToLocalDateTime(l.activation_record_date),
            sid: l.sid,
            computer_name: l.computer_name,
            payment_history_id: l.payment_history_id,
            free_trial_flg: l.free_trial_flg,
            permanent_flg: l.permanent_flg,
            support_start_date: l.support_start_date,
            support_expiration_date: l.support_expiration_date,
            last_support_expiration_date: l.last_support_expiration_date,
            insert_date: formatUtcToLocalDateTime(l.insert_date),
            update_date: formatUtcToLocalDateTime(l.update_date),
            email: l.email,
            family_name: l.family_name,
            given_name: l.given_name,
            organization: l.organization,
        };
        return licenseForOutput;
    });
    return outputList;
}

/**
 * ライセンスタイプと利用開始日時から有効期限日時を計算して返す
 * @param licenseType ライセンスタイプ
 * @param activationDate 利用開始日時
 * @returns 有効期限日時
 */
export function calcExpirationDate(licenseType: number, activationDate: Date) {
    switch (licenseType) {
        case 101:
        case 122:
        case 132:
            // 月間ライセンス
            return addMonths(activationDate, 1);
        case 102:
        case 121:
        case 131:
            // 年間ライセンス
            return addYears(activationDate, 1);
        case 100:
            // 無料体験
            return addDays(activationDate, 14);
        case 200:
        case 201:
        case 202:
            // 期間指定ライセンス
            return addDays(activationDate, 7);
        case 300:
        case 301:
        case 302:
            // 恒久版ライセンス
            break;
        case 0:
            // 内部利用向け
            return addYears(activationDate, 10);
        default:
            break;
    }
    return null;
}

/**
 * ユーザーIDからユーザー情報を特定
 * @param user_id ユーザーID
 * @param userList 検索対象のユーザー情報リスト
 * @returns ユーザー情報
 */
function specifyUser(user_id: string, userList: User[]) {
    if (user_id === null) {
        return null;
    }
    const user: User | undefined = userList.find((u) => u.user_id === user_id);
    return user ? user : null;
}
